'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Financials } from '@benzinga/securities-manager';
import { numberShorthand } from '@benzinga/utils';
import { MetricsTable, MetricItem } from './MetricsTable';

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  const prefix = value < 0 ? '-$' : '$';
  return `${prefix}${numberShorthand(Math.abs(value), 2)}`;
};

const formatShorthand = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return numberShorthand(value, 2);
};

const formatPercent = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '-';
  return `${(value * 100).toFixed(2)}%`;
};

const formatRatio = (value: number | undefined | null): string => {
  if (value === undefined || value === null || value === 0) return '-';
  return value.toFixed(2);
};

type SectionResult = { title: string; metrics: MetricItem[] } | null;
type SectionBuilder = (
  fundamentals: Financials | null,
  fundamentalsAnnual: Financials | null,
  t: TFunction,
) => SectionResult;

const profitability: SectionBuilder = (fundamentals, _annual, t) => {
  const ops = fundamentals?.operationRatios?.[0];
  if (!ops) return null;

  return {
    title: t('Quote.Profitability.title'),
    metrics: [
      { label: t('Quote.Profitability.grossMargin'), value: formatPercent(ops.grossMargin) },
      { label: t('Quote.Profitability.netMargin'), value: formatPercent(ops.netMargin) },
      { label: t('Quote.Profitability.operatingMargin'), value: formatPercent(ops.operationMargin) },
      { label: t('Quote.Profitability.ebitdaMargin'), value: formatPercent(ops.ebitdaMargin) },
      { label: t('Quote.Profitability.roe'), value: formatPercent(ops.roe) },
      { label: t('Quote.Profitability.roa'), value: formatPercent(ops.roa) },
      { label: t('Quote.Profitability.roic'), value: formatPercent(ops.roic) },
    ],
  };
};

const cashFlows: SectionBuilder = (fundamentals, _annual, t) => {
  const cf = fundamentals?.financials?.[0]?.cashFlowStatement;
  if (!cf) return null;

  return {
    title: t('Quote.CashFlows.title'),
    metrics: [
      { label: t('Quote.CashFlows.operatingCashFlow'), value: formatShorthand(cf.operatingCashFlow) },
      { label: t('Quote.CashFlows.investingCashFlow'), value: formatShorthand(cf.investingCashFlow) },
      { label: t('Quote.CashFlows.financingCashFlow'), value: formatShorthand(cf.financingCashFlow) },
      { label: t('Quote.CashFlows.freeCashFlow'), value: formatShorthand(cf.freeCashFlow) },
      { label: t('Quote.CashFlows.capitalExpenditure'), value: formatShorthand(cf.capitalExpenditure) },
      { label: t('Quote.CashFlows.netChangeInCash'), value: formatShorthand(cf.changesInCash) },
    ],
  };
};

const valuation: SectionBuilder = (fundamentals, _annual, t) => {
  const vr = fundamentals?.valuationRatios?.[0];
  const scp = fundamentals?.shareClassProfile;
  if (!vr && !scp) return null;

  return {
    title: t('Quote.Valuation.title'),
    metrics: [
      { label: t('Quote.Valuation.enterpriseValue'), value: formatCurrency(scp?.enterpriseValue) },
      { label: t('Quote.Valuation.peRatioTTM'), value: formatRatio(vr?.peRatio) },
      { label: t('Quote.Valuation.peRatioFWD'), value: formatRatio(vr?.forwardPeRatio) },
      { label: t('Quote.Valuation.pegRatioTTM'), value: formatRatio(vr?.pegRatio) },
      { label: t('Quote.Valuation.psRatioTTM'), value: formatRatio(vr?.psRatio) },
      { label: t('Quote.Valuation.pbRatioTTM'), value: formatRatio(vr?.pbRatio) },
      { label: t('Quote.Valuation.earningsYieldTTM'), value: formatPercent(vr?.earningYield) },
      { label: t('Quote.Valuation.earningsYieldFWD'), value: formatPercent(vr?.forwardEarningYield) },
      { label: t('Quote.Valuation.evToEbitdaTTM'), value: formatRatio(vr?.evToEbitda) },
      { label: t('Quote.Valuation.priceToEbitdaTTM'), value: formatRatio(vr?.pricetoEbitda) },
      { label: t('Quote.Valuation.priceToFreeCashFlow'), value: formatRatio(vr?.fcfRatio) },
      { label: t('Quote.Valuation.bookValuePerShare'), value: formatCurrency(vr?.bookValuePerShare) },
    ],
  };
};

const debt: SectionBuilder = (fundamentals, fundamentalsAnnual, t) => {
  const bs = fundamentals?.financials?.[0]?.balanceSheet;
  const cfAnnual = fundamentalsAnnual?.financials?.[0]?.cashFlowStatement;
  const ops = fundamentals?.operationRatios?.[0];
  if (!fundamentals || (!bs && !cfAnnual)) return null;

  const totalDebt = (bs?.currentDebt ?? 0) + (bs?.longTermDebt ?? 0);

  return {
    title: t('Quote.Debt.title'),
    metrics: [
      { label: t('Quote.Debt.totalDebt'), value: formatCurrency(totalDebt || null) },
      { label: t('Quote.Debt.ltDebt'), value: formatCurrency(bs?.longTermDebt) },
      { label: t('Quote.Debt.ltDebtIssuance'), value: formatCurrency(cfAnnual?.longTermDebtIssuance) },
      { label: t('Quote.Debt.ltDebtRepayment'), value: formatCurrency(cfAnnual?.longTermDebtPayments) },
      { label: t('Quote.Debt.totalCash'), value: formatCurrency(bs?.cashAndCashEquivalents) },
      { label: t('Quote.Debt.debtToEquity'), value: ops?.totalDebtEquityRatio?.toFixed(2) || '-' },
      { label: t('Quote.Debt.issuanceOfDebt'), value: formatCurrency(cfAnnual?.netLongTermDebtIssuance) },
      { label: t('Quote.Debt.repaymentOfDebt'), value: formatCurrency(cfAnnual?.repaymentOfDebt) },
    ],
  };
};

const builders = { profitability, cashFlows, valuation, debt } as const;

export type MetricsSectionType = keyof typeof builders;

interface MetricsSectionProps {
  section: MetricsSectionType;
  fundamentals: Financials | null;
  fundamentalsAnnual?: Financials | null;
}

export const MetricsSection: React.FC<MetricsSectionProps> = ({ section, fundamentals, fundamentalsAnnual }) => {
  const { t } = useTranslation('quote');
  const result = builders[section](fundamentals, fundamentalsAnnual ?? null, t);
  if (!result) return null;
  return <MetricsTable title={result.title} metrics={result.metrics} />;
};

export default MetricsSection;
